import { Alert, Box, Button, FormControl, Icon, InputLabel, MenuItem, Paper, Select, SelectChangeEvent, TextField, Typography } from '@mui/material'
import { useAppSelector } from 'app/hooks'
import { LOADING_OPTION } from 'enums'
import { getEmpresaListSelector, getUsuarioSelector } from 'features'
import Lottie from 'lottie-react'
import { Dashboard } from 'pages'
import React from 'react'
import { useDropzone } from 'react-dropzone'
import { uploadPlantillaCSVAsyncService } from 'services'
import { IEmpresa } from 'types'
import uploadingAnimation from '../../../assets/lotties/UploadingFile.json'
import { useLoading } from '../../../hooks/useLoading'

type ServerMessage = {
    type: 'success' | 'error'
    text: string
}
export const CargaMasivaAsync = () => {
    const usuario = useAppSelector(getUsuarioSelector)
    const empresaList = useAppSelector(getEmpresaListSelector)
    const [empresa, setEmpresa] = React.useState<number>(0)
    const [email, setEmail] = React.useState<string>(usuario?.correo || '')
    const [emailError, setEmailError] = React.useState<boolean>(false)
    const [selectedFile, setSelectedFile] = React.useState<File | null>(null)
    const [serverMessage, setServerMessage] = React.useState<ServerMessage | null>(null)
    const { loading, handleChangeLoading, handleCloseLoad } = useLoading<LOADING_OPTION>()
    const uploadingCSV = loading.includes(LOADING_OPTION.UPLOAD_CSV)

    const handleChangeEmpresa = (empresas: IEmpresa[]) => {
        if (empresas.length > 0) {
            setEmpresa(empresas[0].id)
        } else {
            setEmpresa(0)
        }
    }

    const validateExtencionDocument = (filename: string) => {
        const validExtensions = ['csv']
        const extension = filename.split('.').pop()?.toLowerCase()
        return validExtensions.includes(extension || '')
    }

    const filteredEmpresas = React.useMemo(() => {
        const validEmpresas: IEmpresa[] = []
        if (usuario !== undefined) {
            if (usuario?.permisoSuperdeporte && empresaList[0]) {
                validEmpresas.push(empresaList[0])
            }
            if (usuario?.permisoMedeportAdidas && empresaList[1]) {
                validEmpresas.push(empresaList[1])
            }
            if (usuario?.permisoMedeportReebok && empresaList[2]) {
                validEmpresas.push(empresaList[2])
            }
            if (usuario?.permisoEquinox && empresaList[3]) {
                validEmpresas.push(empresaList[3])
            }
        }
        handleChangeEmpresa(validEmpresas)
        return validEmpresas
    }, [empresaList, usuario])

    const handleUploadFile = async (file: File) => {
        try {
            handleChangeLoading(LOADING_OPTION.UPLOAD_CSV)
            if (file) {
                const response = await uploadPlantillaCSVAsyncService(file, empresa, usuario!.usuarioId, email)
                if (response.status === 200) {
                    setServerMessage({ type: 'success', text: response.data })
                }
            }
        } catch (error: any) {
            setServerMessage({ type: 'error', text: error?.response?.data })
        } finally {
            handleCloseLoad(LOADING_OPTION.UPLOAD_CSV)
        }
    }

    const onDrop = (acceptedFiles: File[]) => {
        if (acceptedFiles.length > 0) {
            const file = acceptedFiles[0]
            if (!validateExtencionDocument(file.name)) {
                setServerMessage({ type: 'error', text: 'La extención del archivo no es CSV' })
                return
            }
            setSelectedFile(file)
            setServerMessage(null)
        }
    }

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: { 'text/csv': ['.csv'] },
        multiple: false,
    })

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setEmail(value)
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        setEmailError(!emailRegex.test(value))
    }

    return (
        <Dashboard title="Subida de clientes">
            <Box
                sx={{
                    display: 'flex',
                    width: '100%',
                    height: '80vh',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        width: '100%',
                        height: '100%',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            width: '100%',
                            justifyContent: 'flex-start',
                            gap: 4,
                        }}
                    >
                        <FormControl size="small">
                            <InputLabel sx={{ color: '#C4C4C4' }}>Empresa</InputLabel>
                            <Select
                                value={empresa !== undefined ? empresa.toString() : ''}
                                onChange={async (event: SelectChangeEvent) => {
                                    const empresaSelected = Number.parseInt(event.target.value)
                                    setEmpresa(empresaSelected)
                                }}
                                sx={{ width: '240px' }}
                                label="Empresa"
                                style={{ borderRadius: '0px' }}
                            >
                                {filteredEmpresas.map((empresa) =>
                                    empresa ? (
                                        <MenuItem key={empresa.id} value={empresa.id}>
                                            {empresa.nombre}
                                        </MenuItem>
                                    ) : null
                                )}
                            </Select>
                        </FormControl>

                        <TextField
                            size="small"
                            label="Correo para resumen"
                            variant="outlined"
                            value={email}
                            onChange={handleEmailChange}
                            error={emailError}
                            helperText={emailError ? 'Correo no válido' : 'Puedes cambiar el correo que recibirá el resumen de la subida del archivo CSV'}
                            sx={{ width: '300px' }}
                            inputProps={{ type: 'email' }}
                        />
                    </Box>

                    <Box
                        sx={{
                            py: 2.5,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            width: '100%',
                        }}
                    >
                        {!selectedFile ? (
                            <Paper
                                elevation={3}
                                sx={{
                                    padding: 2,
                                    width: '50%',
                                    height: '200px',
                                    maxWidth: '50%',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    border: '2px dashed #ccc',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    borderRadius: 4,
                                    my: 0.5,
                                    pointerEvents: uploadingCSV ? 'none' : 'auto',
                                }}
                                {...getRootProps()}
                            >
                                <input {...getInputProps()} disabled={uploadingCSV} />
                                {!uploadingCSV ? (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Icon sx={{ fontSize: 100, color: '#aaa' }}>cloud_upload</Icon>
                                        <Typography variant="h6" color="textSecondary">
                                            Arrastra y suelta un archivo CSV aquí, o haz clic para seleccionar uno
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            width: '100%',
                                        }}
                                    >
                                        <Lottie animationData={uploadingAnimation} loop={true} style={{ width: 370, height: 280 }} />
                                    </Box>
                                )}
                            </Paper>
                        ) : (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    width: '50%',
                                    maxWidth: '50%',
                                    border: '1px solid #ccc',
                                    borderRadius: 1,
                                    padding: 1,
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Typography noWrap sx={{ flexGrow: 1 }}>
                                    Archivo seleccionado: <strong>{selectedFile.name}</strong>
                                </Typography>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        background: '#DB2804',
                                        '&:hover': {
                                            background: '#DB2804',
                                            opacity: 0.8,
                                        },
                                    }}
                                    onClick={() => {
                                        setSelectedFile(null)
                                        setServerMessage(null)
                                    }}
                                >
                                    X
                                </Button>

                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => handleUploadFile(selectedFile)}
                                    disabled={uploadingCSV || emailError}
                                    sx={{
                                        textTransform: 'none',
                                    }}
                                >
                                    Procesar archivo
                                </Button>
                            </Box>
                        )}

                        {/* Mensaje del servidor */}
                        {serverMessage && (
                            <Box sx={{ width: '50%', mt: 2 }}>
                                <Alert severity={serverMessage.type} variant="filled">
                                    {serverMessage.text}
                                </Alert>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </Dashboard>
    )
}
